'use client';

import { MetaDefault } from '../../../../../components/typography';
import type { CardRenderContext } from '../../tool-config-types';
import {
  parseHarEntriesFromMarkdown,
  parseHarEntriesFromObject,
} from '../../content-utils/content-parsers';
import type { ParsedHarEntry } from '../../ToolCallsRenderers/types';
import { HttpExecutionCard } from '../../../../artifacts/http/HttpExecutionCard';
import { InlineExecutionToolCard } from '../shared/inline-execution-tool-card';
import { getHITLArtifactHeaderActions } from '../shared/hitl-artifact-header-actions';
import { formatCountedToolCallLabel } from '../shared/format-counted-tool-call-label';
import { HttpExchangeRows } from '../shared/http-exchange-rows';
import { toPrettyText } from '../shared/network-tool-card-utils';

const parseHarEntriesFromToolOutput = (
  content: unknown,
): {
  entries: ParsedHarEntry[];
  query?: string;
  totalEntries?: number;
} | null => {
  if (typeof content === 'object' && content !== null) {
    return parseHarEntriesFromObject(content);
  }

  if (typeof content !== 'string') {
    return null;
  }

  try {
    const parsed = JSON.parse(content);

    return parseHarEntriesFromObject(parsed);
  } catch {
    return (
      parseCapturedHttpExchanges(content) ??
      parseHarEntriesFromMarkdown(content) ??
      parseHarEntriesFromText(content)
    );
  }
};

const parseHarEntriesFromText = (
  content: string,
): {
  entries: ParsedHarEntry[];
  query?: string;
  totalEntries?: number;
} | null => {
  const entries: ParsedHarEntry[] = [];
  const headingMatch = content.match(/HAR Entries for\s+(.+?)(?:\n|$)/i);
  const bulletPattern =
    /^\s*[-*]\s+(?:\*\*)?([A-Z]+)\s+(.+?)(?:\*\*)?\s*$([\s\S]*?)(?=^\s*[-*]\s+(?:\*\*)?[A-Z]+\s+.+?(?:\*\*)?\s*$|\s*$)/gm;

  for (const match of content.matchAll(bulletPattern)) {
    const detail = match[3]?.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();
    const responseStatus = detail?.match(/Status:\s*(\d+)/)?.[1];
    const matchReason =
      detail && detail.length > 260 ? `${detail.slice(0, 260)}...` : detail;

    entries.push({
      id: `${match[1]}-${match[2]}-${entries.length}`,
      method: match[1],
      url: match[2].trim(),
      responseStatus,
      matchReason,
    });
  }

  if (entries.length === 0) {
    return null;
  }

  return {
    entries,
    query: headingMatch?.[1]?.trim(),
  };
};

export function parseCapturedHttpExchanges(content: string): {
  entries: ParsedHarEntry[];
  totalEntries?: number;
} | null {
  const entries: ParsedHarEntry[] = [];
  const linePattern =
    /^\s*-\s+id:\s+(\S+)\s+[—-]\s+([A-Z]+)\s+(\S+)\s+→\s+(\d+)/gm;

  for (const match of content.matchAll(linePattern)) {
    entries.push({
      id: match[1],
      method: match[2],
      url: match[3],
      responseStatus: match[4],
    });
  }

  if (entries.length === 0) {
    return null;
  }

  const count = content.match(
    /Captured HTTP exchanges in this session \((\d+)\)/,
  );

  return {
    entries,
    totalEntries: count ? Number(count[1]) : entries.length,
  };
}

function readHttpFence(section: string): string | undefined {
  return section.match(/```(?:http)?\s*([\s\S]*?)```/)?.[1]?.trim();
}

export function parseVerbatimHttpExchange(content: string): {
  requestPayload: string;
  responsePayload: string;
} | null {
  const requestHeading = content.indexOf('### Request');
  const responseHeading = content.indexOf('### Response');

  if (requestHeading < 0 || responseHeading < requestHeading) {
    return null;
  }

  const requestFence = readHttpFence(
    content.slice(requestHeading, responseHeading),
  );
  const responseFence = readHttpFence(content.slice(responseHeading));

  if (!requestFence || !responseFence) {
    return null;
  }

  return {
    requestPayload: requestFence,
    responsePayload: responseFence,
  };
}

export function renderHarEntriesCard(ctx: CardRenderContext) {
  const isRunning =
    ctx.toolCallData.status === 'started' ||
    ctx.toolCallData.status === 'in-progress';
  const outputText =
    typeof ctx.toolCallData.output === 'string'
      ? ctx.toolCallData.output
      : undefined;
  const verbatimExchange = outputText
    ? parseVerbatimHttpExchange(outputText)
    : null;
  const parsed = verbatimExchange
    ? null
    : parseHarEntriesFromToolOutput(ctx.toolCallData.output);
  const entries = parsed?.entries ?? [];
  const fallbackText =
    entries.length === 0 && !verbatimExchange
      ? toPrettyText(ctx.toolCallData.output)
      : undefined;

  if (
    !isRunning &&
    entries.length === 0 &&
    !verbatimExchange &&
    !fallbackText &&
    !ctx.toolCallData.error &&
    ctx.toolCallData.status === 'completed'
  ) {
    return null;
  }

  const displayText = isRunning
    ? ctx.labelPresent
    : entries.length > 0
      ? formatCountedToolCallLabel({
          label: ctx.labelPast,
          count: entries.length,
        })
      : ctx.labelPast;

  return (
    <InlineExecutionToolCard
      title={displayText}
      status={ctx.toolCallData.status}
      error={ctx.toolCallData.error}
      headerActions={getHITLArtifactHeaderActions(ctx)}
      defaultExpanded={false}
      autoCollapseWhenComplete={ctx.autoCollapseWhenComplete}
      forceExpanded={ctx.forceExpanded}
    >
      {verbatimExchange ? (
        <HttpExecutionCard
          requestPayload={verbatimExchange.requestPayload}
          responsePayload={verbatimExchange.responsePayload}
        />
      ) : null}
      {entries.length > 0 ? <HttpExchangeRows entries={entries} /> : null}
      {fallbackText ? (
        <MetaDefault color="fg.muted" whiteSpace="pre-wrap">
          {fallbackText}
        </MetaDefault>
      ) : null}
    </InlineExecutionToolCard>
  );
}
